import React, { useEffect, useState } from 'react';
import CommandPanelImage from 'components/commandpanel/CommandPanelImage';
import {
  isNullOrUndefined,
  GetExpInspections,
  isCtrlKeyPressed,
  GetDisplayMode,
} from 'utils/Global';
import { ModalDetails } from 'business/models/CustomModels';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import { selectNavigatorAvailableImagesKeys } from 'redux/selectors/StateSelectors';
import RMModal from 'components/modal/RMModal';
import { getNavigatorCommandPanelImageIcons } from 'utils/Navigator/NavigatorUtils';
import {
  useNavigatorInspections,
  usePlateDrawaingIdentifiers,
  useUpdateCompositeImage,
} from './hooks';
import { useWellDropCompareMode } from 'redux/hooks/well';
import {
  MAX_COMPARE_IMAGE_SELECTION,
  MAX_COMPARE_OVERLAY_IMAGE_SELECTION,
} from 'components/well/constants';
import { getNavFilteredScore, getNavigatorImageKeysAsString } from './utils';
import { NavigatorProps } from './NavigatorThumbnail';
import { updateDropPath } from 'utils/ExperimentUtils';
import { DropImageParameters, DropUrlParameters } from 'components/Experiment/constants';
import { useAllDropImageParameters, useScoresForWells } from 'redux/hooks/experiment';
import { setWellImagingSettingDetails } from 'redux/reducers/experimentDataReducer';
import { useLocation, useNavigate } from 'react-router-dom';

const NavigatorInspections = (props: NavigatorProps) => {
  const { setMoveToCanvas, wellNavigationDetails, dropImageParameters } = props;
  const [modalDetails, setModalDetails] = useState<ModalDetails>({
    showModal: false,
    setShowModal: () => {},
    onOk: () => {},
    body: null,
    heading: '',
    isOkOnly: false,
    okButtonText: undefined,
    showButtons: true,
  });
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useRMDispatch();
  const inspections = GetExpInspections();
  const navAvailableImages = useRMSelector(selectNavigatorAvailableImagesKeys);
  const selectDrawingIdentifiers = usePlateDrawaingIdentifiers();
  const updateCompositeImage = useUpdateCompositeImage();
  const allWellImagingSettingDetails = useAllDropImageParameters();
  const { wellNum, plateID } = wellNavigationDetails || {};
  const { cpId, dropNumber, inspection, regionName } = dropImageParameters || {};
  const plateDrawings = selectDrawingIdentifiers(plateID);
  const disabledImageRegions = plateDrawings?.plateDropRegions;
  const displayMode = GetDisplayMode();
  const { isCompareModeOverlay } = useWellDropCompareMode();
  const currentWellScores = useScoresForWells([
    {
      wellNumber: wellNum,
      plateId: plateID,
      dropNumber: dropNumber,
    },
  ]);

  function isActiveInspection(batchId) {
    const detailsToCheck = isCompareModeOverlay
      ? allWellImagingSettingDetails.slice(-MAX_COMPARE_OVERLAY_IMAGE_SELECTION)
      : allWellImagingSettingDetails;
    if (detailsToCheck.length > 1) {
      return detailsToCheck.find((details) =>
        details.inspection.groupedInspections.find((gi) => gi.imageBatchId === batchId)
          ? true
          : false,
      );
    } else {
      return inspection?.groupedInspections?.find((gi) => gi.imageBatchId === batchId)
        ? true
        : false;
    }
  }

  const reloadExperiment = () => {
    window.location.reload();
  };

  const [navigatorInspections, setNavigatorInpections] = useState(null);

  useEffect(() => {
    const compositeKey = getNavigatorImageKeysAsString(
      wellNavigationDetails,
      dropImageParameters,
    );
    if (navAvailableImages.includes(compositeKey)) {
      setNavigatorInpections(useNavigatorInspections(compositeKey));
    }
  }, [navAvailableImages, wellNavigationDetails, dropImageParameters]);

  useEffect(() => {
    updateCompositeImage(wellNavigationDetails, dropImageParameters);
  }, [displayMode]);

  function onInspectionChange(value, ctrlKey) {
    const inspectionBatchId = parseInt(value, 10);
    const selectedinspectionSet = inspections.find((inspSet) =>
      inspSet.groupedInspections.some((gi) => gi.imageBatchId == inspectionBatchId),
    );
    if (!selectedinspectionSet) {
      setModalDetails({
        ...modalDetails,
        showModal: true,
        heading: 'Reload',
        body: (
          <div id="navigatorinspections-reload">
            Selected data needs to be reloaded. Reload current Experiment?
          </div>
        ),
        showButtons: true,
        okButtonText: 'Reload',
        noButtonText: 'Cancel',
        onOk: reloadExperiment,
      });
      return;
    }
    if (!ctrlKey) {
      const imageBatchId = selectedinspectionSet.groupedInspections.find(
        (insp) => insp.plateId == plateID,
      )?.imageBatchId;
      updateDropPath(
        location.pathname,
        [{ index: DropUrlParameters.IMAGEBATCHID, value: imageBatchId }],
        navigate,
      );
    }

    const navigationDetails: DropImageParameters = {
      ...dropImageParameters,
      inspection: selectedinspectionSet,
    };

    updateCompositeImage(wellNavigationDetails, navigationDetails);

    const selectedNav = allWellImagingSettingDetails.find(
      (w) =>
        w.dropNumber === navigationDetails.dropNumber &&
        w.cpId === navigationDetails.cpId &&
        w.inspection.groupedInspections.find(
          (i) => i.imageBatchId === inspectionBatchId,
        ) &&
        w.regionName === navigationDetails.regionName,
    );

    if (
      allWellImagingSettingDetails.length === 1 &&
      !isNullOrUndefined(selectedNav) &&
      ctrlKey
    ) {
      return;
    }
    const filteredNavDetails = allWellImagingSettingDetails.filter(
      (w) =>
        w.dropNumber === navigationDetails.dropNumber &&
        w.cpId === navigationDetails.cpId &&
        w.inspection.groupedInspections.find(
          (i) => i.imageBatchId !== inspectionBatchId,
        ) &&
        w.regionName === navigationDetails.regionName,
    );
    if (!isNullOrUndefined(selectedNav) && filteredNavDetails.length && ctrlKey) {
      dispatch(setWellImagingSettingDetails([...filteredNavDetails]));
      return;
    }
    if (ctrlKey) {
      const maxSelection = isCompareModeOverlay
        ? MAX_COMPARE_OVERLAY_IMAGE_SELECTION
        : MAX_COMPARE_IMAGE_SELECTION;
      if (isCompareModeOverlay && allWellImagingSettingDetails.length >= maxSelection) {
        dispatch(
          setWellImagingSettingDetails([
            ...allWellImagingSettingDetails.slice(1),
            navigationDetails,
          ]),
        );
      } else if (allWellImagingSettingDetails.length < maxSelection) {
        dispatch(
          setWellImagingSettingDetails([...filteredNavDetails, navigationDetails]),
        );
      }
      setMoveToCanvas(true);
    } else {
      dispatch(setWellImagingSettingDetails([navigationDetails]));
    }
  }

  function getInspectionsView() {
    return !isNullOrUndefined(navigatorInspections)
      ? navigatorInspections?.map((inspection, i) => (
          <CommandPanelImage
            key={`ins-${inspection.batchId}`}
            image={inspection.image}
            inspectionName={inspection.name}
            imageName={inspection.imageName}
            inspectionDateTime={inspection.dateImaged}
            active={isActiveInspection(inspection.batchId)}
            onClick={(e) => onInspectionChange(inspection.batchId, isCtrlKeyPressed(e))}
            iconImages={getNavigatorCommandPanelImageIcons(
              plateDrawings,
              disabledImageRegions,
              wellNum,
              dropNumber,
              regionName,
              cpId,
              inspection.batchId,
            )}
            currentWellScores={getNavFilteredScore(
              currentWellScores,
              wellNum,
              dropNumber,
              inspection.batchId,
            )}
          />
        ))
      : null;
  }

  return (
    <>
      <div id="navigatorinspections-container" className="tabContainer">
        {getInspectionsView()}
      </div>
      {modalDetails.showModal && (
        <RMModal
          id="navigatorinspections-modal"
          showModal={modalDetails.showModal}
          setShowModal={() => setModalDetails({ ...modalDetails, showModal: false })}
          heading={modalDetails.heading}
          body={modalDetails.body}
          showButtons={modalDetails.showButtons}
          okButtonText={modalDetails.okButtonText}
          noButtonText={modalDetails.noButtonText}
          onOk={modalDetails.onOk}
          onCancel={() => setModalDetails({ ...modalDetails, showModal: false })}
          isOkOnly={modalDetails.isOkOnly}
        />
      )}
    </>
  );
};

export default NavigatorInspections;
